import {Suspense, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {offer, handleSetOfferId, latestQuery, mapOfferListQuery, handleQuickViewOpen, propertyListType, onPropertyListTypeClick} = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const {shouldHidePrices} = useShouldHidePrices();
    const shouldShowPrice = !shouldHidePrices;

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div className={wrapper}>
                <div className={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={offer.vendor.logo?.v_log_120x90} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div className={contentStyle}>
                    <div className={infoBoxWrapper}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />

                        <div className={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" className={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {shouldShowPrice && !!offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer?.stats?.ranges_price_m2_min ?? 0)}`} {getCurrency({country: offer.region.country})}/m
                                        <sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>

                    <div className={tilesHeaderWrapper} ref={headerRef}>
                        <div className={headerButtonContainer}>
                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TABLE && listTypeActiveButton)}
                            >
                                Rzuty
                            </Button>

                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TILES && listTypeActiveButton)}
                            >
                                Lista
                            </Button>
                        </div>

                        <div className={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" className={css({ml: 1})} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <div className={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                className={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleCellClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground="var(--colors-gray-100)"
                                gtmMeta={{
                                    listId: "zobacz_na_mapie",
                                    listName: "Zobacz na mapie"
                                }}
                            />
                        </div>
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            className={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            gtmMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            className={css({mb: 2})}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css({
    width: "100%"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    backgroundColor: "var(--colors-white)"
});

const modalPropertyList = css({
    backgroundColor: "var(--colors-gray-100) !important"
});

const tableWrapper = css({
    width: "100vw",
    ml: 1.5,

    xs: {
        width: "100%",
        ml: "initial"
    }
});

const tilesHeaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
});

const listTypeButton = css({
    p: 1,

    "&:hover": {
        color: "var(--colors-secondary)",
        fill: "var(--colors-secondary)"
    }
});

const listTypeActiveButton = css({
    color: "var(--colors-gray-600)",
    fill: "var(--colors-gray-600)"
});

const headerButtonContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    gap: 2
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    px: 1.5,
    maxHeight: "calc(100dvh - 60px - 54px)",
    overflowX: "hidden",
    overflowY: "auto",
    pb: 6
});

const infoBoxWrapper = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 2
});

const infoBoxContainer = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 0.5
});

const constructionDatePill = css({
    display: "flex",
    alignItems: "center",
    p: 0.5,
    gap: 0.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-primary)"
});
import {Suspense, useRef, useState} from "react";
import {css, cx} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {CalendarCheckIcon, FiltersIcon, HorizontalListIcon, PropertyPlanIcon, SortIcon} from "@pg-design/icons-module";
import {Image} from "@pg-design/image-module";
import {Loader} from "@pg-design/loader-module";
import {Pagination, PaginationSize as PS} from "@pg-design/pagination-module";
import {Highlight, Text} from "@pg-design/text-module";
import {fromQueryValues, toQueryValues} from "@pg-mono/form";
import {isEmpty, omit} from "@pg-mono/nodash";
import {dateTimeFormat, formatFutureDate, numberFormat} from "@pg-mono/string-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {useShouldHidePrices} from "../../../hooks/use_should_hide_prices";
import {IFiltersFormValues, PropertyListFiltersModal} from "../../../property/components/PropertyListFiltersModal";
import {PropertyListSortOptionsModal} from "../../../property/components/PropertyListSortOptionsModal";
import {propertyListSortOptions} from "../../../property/constants/property_list_sort_options";
import {usePropertyList} from "../../../property_list/hooks/use_property_list";
import {ISearchFiltersFormValues} from "../../../property_list/PropertyListFilters";
import {PropertyListTable} from "../../../property_list/PropertyListTable";
import {PropertyListType} from "../../../property_list/PropertyListTabSwitcher";
import {PropertyListTileList} from "../../../property_list/PropertyListTileList";
import {getCurrency} from "../../../utils/get_currency";
import {ILatestQuery} from "../../../utils/latest_query";
import {PropertyListTabTypes} from "../../detail/actions/fetch_offer_detail_properties";
import {propertyListTableFiltersFields} from "../../detail/components/property_list/OfferDetailPropertyListTable";
import {SortOptionsValues} from "../../detail/types/SortOptionsValues";
import {IUseMapOfferListQueryResult} from "../../hooks/use_map_offer_list_query";
import {DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE} from "../../list/components/OfferListPropertiesModal";
import {IMapOfferSidebar} from "../../map/types/MapOfferSidebar";
import {IOfferDetail} from "../../types/IOfferDetail";
import {MapOfferExpandablePanelOfferSwitcher} from "./MapOfferExpandablePanelOfferSwitcher";

interface IProps {
    offer: IMapOfferSidebar;
    handleSetOfferId: (offerId: number | null) => void;
    latestQuery: ILatestQuery;
    mapOfferListQuery?: IUseMapOfferListQueryResult;
    handleQuickViewOpen: (id: number) => void;
    onPropertyListTypeClick: (value: PropertyListType) => void;
    propertyListType: PropertyListType;
}

export const MapOfferExpandablePanelOffer = (props: IProps) => {
    const {offer, handleSetOfferId, latestQuery, mapOfferListQuery, handleQuickViewOpen, propertyListType, onPropertyListTypeClick} = props;

    const latestSearchFilters = fromQueryValues<ISearchFiltersFormValues>(omit(propertyListTableFiltersFields, ["price"]), latestQuery);
    const propertyListFilters = isEmpty(latestSearchFilters) ? {} : toQueryValues(propertyListTableFiltersFields, latestSearchFilters);

    const propertyList = usePropertyList(offer.id, PropertyListTabTypes.MODAL, undefined, DEFAULT_PROPERTY_LIST_MODAL_PAGE_SIZE);
    const {page, pageCount, requestState, properties, sort, onPageChange, pageIsLoading, onSortChange, onFiltersChange} = propertyList;

    const {isMobile} = useUserDevice();
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
    const [isSortModalOpen, setIsSortModalOpen] = useState(false);
    const headerRef = useRef<HTMLDivElement>(null);

    const {shouldHidePrices} = useShouldHidePrices();
    const shouldShowPrice = !shouldHidePrices;

    const handlers = {
        closeFilters: () => {
            setIsFiltersModalOpen(false);
        },
        saveFilters: (values: IFiltersFormValues) => {
            const floorChoicesMap: {[index: number]: boolean} = {
                1: values.floorGround,
                2: values.floorGroundWithGarden,
                3: values.floorOneToFour,
                4: values.floorFivePlus,
                5: values.floorLast,
                6: values.floorLastWithTerrace
            };

            const floorChoices = Object.keys(floorChoicesMap)
                .filter((floorChoice) => Boolean(floorChoicesMap[parseInt(floorChoice)]))
                .map((floorChoice) => parseInt(floorChoice));
            onFiltersChange({
                ...(values.area?.lower && {area_0: values.area.lower}),
                ...(values.area?.upper && {area_1: values.area.upper}),
                ...(values.rooms?.lower && {rooms_0: values.rooms.lower}),
                ...(values.rooms?.upper && {rooms_1: values.rooms.upper}),
                floor_choices: floorChoices
            });
            setIsFiltersModalOpen(false);
        },
        saveSort: (value: number) => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === value)?.slug || null;
            onSortChange(sortSlugValue);
            setIsSortModalOpen(false);
        },
        closeSort: () => {
            setIsSortModalOpen(false);
        },
        openSort: () => {
            setIsSortModalOpen(true);
        },
        openFilters: () => {
            setIsFiltersModalOpen(true);
        },
        clearSort: () => {
            const sortSlugValue = propertyListSortOptions.find((option) => option.value === SortOptionsValues.LOWEST_PRICE)?.slug || null;
            onSortChange(sortSlugValue, propertyListFilters);
            setIsSortModalOpen(false);
        },
        handlePageChange: async (page: number) => {
            onPageChange(page);
            headerRef.current?.scrollIntoView({behavior: "smooth"});
        }
    };

    return (
        <Suspense fallback={<Loader />}>
            <div className={wrapper}>
                <div className={headerStyle}>
                    <Image alt="logo dewelopera" width="72px" height="54px" src={offer.vendor.logo?.v_log_120x90} />

                    <MapOfferExpandablePanelOfferSwitcher
                        mapOfferListQuery={mapOfferListQuery}
                        offerId={offer.id}
                        handleSetOfferId={handleSetOfferId}
                        vendorId={offer.vendor.id}
                        propertyListType={propertyListType}
                    />
                </div>

                <div className={contentStyle}>
                    <div className={infoBoxWrapper}>
                        <Image alt={offer.name} src={offer.main_image.m_img_224x147} width="123px" height="116px" loading="lazy" />

                        <div className={infoBoxContainer}>
                            <Text as="div" variant="info_txt_2" className={constructionDatePill}>
                                <CalendarCheckIcon size="1.2" wrapperSize="1.2" wrapperColor="transparent" />

                                <Text as="span" variant="info_txt_2" strong>
                                    {formatFutureDate(offer.construction_date_range.upper, dateTimeFormat.quarter)}
                                </Text>
                            </Text>

                            <Text>{offer.name}</Text>

                            {shouldShowPrice && !!offer?.stats?.ranges_price_m2_min && (
                                <Text as="div" variant="body_copy_1" mb={1.5} strong>
                                    <Highlight>
                                        {`od ${numberFormat(offer?.stats?.ranges_price_m2_min ?? 0)}`} {getCurrency({country: offer.region.country})}/m
                                        <sup>2</sup>
                                    </Highlight>
                                </Text>
                            )}

                            <Text variant="info_txt_2">{offer.address}</Text>

                            {!!offer.stats?.distance_from_region.distance && (
                                <Text variant="info_txt_2" strong>{`(${offer.stats.distance_from_region.distance} km od centrum)`}</Text>
                            )}
                        </div>
                    </div>

                    <div className={tilesHeaderWrapper} ref={headerRef}>
                        <div className={headerButtonContainer}>
                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TILES)}
                                iconLeft={PropertyPlanIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TABLE && listTypeActiveButton)}
                            >
                                Rzuty
                            </Button>

                            <Button
                                variant="none_secondary"
                                onClick={() => onPropertyListTypeClick(PropertyListType.TABLE)}
                                iconLeft={HorizontalListIcon}
                                className={cx(listTypeButton, propertyListType === PropertyListType.TILES && listTypeActiveButton)}
                            >
                                Lista
                            </Button>
                        </div>

                        <div className={headerButtonContainer}>
                            <FiltersIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" onClick={handlers.openFilters} />
                            <SortIcon size="2.4" wrapperSize="2.4" wrapperColor="transparent" className={css({ml: 1})} onClick={handlers.openSort} />
                        </div>
                    </div>

                    {propertyListType === PropertyListType.TABLE && (
                        <div className={tableWrapper}>
                            <PropertyListTable
                                requestState={requestState}
                                offer={offer}
                                properties={properties}
                                sort={sort.currentSort}
                                onSortChange={(value) => onSortChange(value, propertyListFilters)}
                                onPageChange={handlers.handlePageChange}
                                pageCount={pageCount}
                                pageIsLoading={pageIsLoading}
                                currentPage={page.currentPage}
                                className={modalPropertyList}
                                hideCell={{
                                    propertyNumber: true
                                }}
                                withoutLink
                                handleCellClick={handleQuickViewOpen}
                                textLinkButton
                                loaderWrapperBackground="var(--colors-gray-100)"
                                gtmMeta={{
                                    listId: "zobacz_na_mapie",
                                    listName: "Zobacz na mapie"
                                }}
                            />
                        </div>
                    )}

                    {propertyListType === PropertyListType.TILES && (
                        <PropertyListTileList
                            requestState={requestState}
                            properties={properties}
                            offer={offer as unknown as IOfferDetail}
                            className={modalPropertyList}
                            gridLayoutCols={{md: 2, lg: 2}}
                            onPropertyTileClick={(property) => handleQuickViewOpen(properties.findIndex(({id}) => id === property.id))}
                            propertyBoxComponent="div"
                            gtmMeta={{
                                listId: "zobacz_na_mapie",
                                listName: "Zobacz na mapie"
                            }}
                        />
                    )}

                    {pageCount > 0 && (
                        <Pagination
                            className={css({mb: 2})}
                            currentPage={page.currentPage}
                            pageCount={pageCount}
                            onPageChange={handlers.handlePageChange}
                            size={isMobile ? PS.SM : PS.MD}
                            siblingsCount={isMobile ? 0 : 1}
                        />
                    )}
                </div>
            </div>

            <PropertyListFiltersModal
                offerType={offer.type}
                isOpen={isFiltersModalOpen && !!isMobile}
                handleClose={handlers.closeFilters}
                handleConfirm={handlers.saveFilters}
            />

            <PropertyListSortOptionsModal
                onClear={handlers.clearSort}
                onSave={handlers.saveSort}
                onClose={handlers.closeSort}
                isOpen={isSortModalOpen}
                defaultValue={propertyListSortOptions.find((option) => option.slug === sort.currentSort)?.value || SortOptionsValues.LOWEST_PRICE}
            />
        </Suspense>
    );
};

const wrapper = css({
    width: "100%"
});

const headerStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 1.5,
    backgroundColor: "var(--colors-white)"
});

const modalPropertyList = css({
    backgroundColor: "var(--colors-gray-100) !important"
});

const tableWrapper = css({
    width: "100vw",
    ml: 1.5,

    xs: {
        width: "100%",
        ml: "initial"
    }
});

const tilesHeaderWrapper = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%"
});

const listTypeButton = css({
    p: 1,

    "&:hover": {
        color: "var(--colors-secondary)",
        fill: "var(--colors-secondary)"
    }
});

const listTypeActiveButton = css({
    color: "var(--colors-gray-600)",
    fill: "var(--colors-gray-600)"
});

const headerButtonContainer = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "normal",
    gap: 2
});

const contentStyle = css({
    backgroundColor: "var(--colors-gray-100)",
    px: 1.5,
    maxHeight: "calc(100dvh - 60px - 54px)",
    overflowX: "hidden",
    overflowY: "auto",
    pb: 6
});

const infoBoxWrapper = css({
    display: "flex",
    alignItems: "flex-start",
    gap: 1,
    py: 2
});

const infoBoxContainer = css({
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    gap: 0.5
});

const constructionDatePill = css({
    display: "flex",
    alignItems: "center",
    p: 0.5,
    gap: 0.5,
    borderRadius: 2,
    backgroundColor: "var(--colors-primary)"
});
